const CUSTOM_SIZE_CODE = "P";

const parseNumericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const normalizeTextValue = (value, uppercase = false) => {
  if (value === null || value === undefined) return null;
  const normalized = String(value).trim();
  if (!normalized) return null;
  return uppercase ? normalized.toUpperCase() : normalized;
};

const normalizeMaximumSizeObject = (maximumSize) => {
  if (!maximumSize || typeof maximumSize !== "object") return null;

  const id = parseNumericValue(maximumSize.id);
  const code = normalizeTextValue(maximumSize.code, true);
  const order = parseNumericValue(maximumSize.order);

  if (id === null || id <= 0 || !code || order === null || order <= 0) return null;

  return {
    id,
    code,
    description: normalizeTextValue(maximumSize.description) || code,
    order,
  };
};

const getLegacyMaximumSizeFamily = (point, family) => {
  const suffixes = family.spanish
    ? { id: "Id", code: "Codigo", description: "Descripcion", order: "Orden" }
    : { id: "Id", code: "Code", description: "Description", order: "Order" };

  return {
    id: point?.[`${family.prefix}${suffixes.id}`],
    code: point?.[`${family.prefix}${suffixes.code}`],
    description: point?.[`${family.prefix}${suffixes.description}`],
    order: point?.[`${family.prefix}${suffixes.order}`],
  };
};

// Prefer the canonical nested maximumSize contract. Remove this temporary adapter when old endpoints stop returning flat DTO families.
const LEGACY_MAXIMUM_SIZE_FAMILIES = [
  { prefix: "MaximumSize", spanish: false },
  { prefix: "maximumSize", spanish: false },
  { prefix: "MaxSize", spanish: false },
  { prefix: "TallaMaxima", spanish: true },
  { prefix: "tallaMaxima", spanish: true },
];

const normalizePointMaximumSize = (point) => {
  if (Object.prototype.hasOwnProperty.call(point || {}, "maximumSize")) {
    return normalizeMaximumSizeObject(point.maximumSize);
  }

  for (const family of LEGACY_MAXIMUM_SIZE_FAMILIES) {
    const legacyMaximumSize = normalizeMaximumSizeObject(getLegacyMaximumSizeFamily(point, family));
    if (legacyMaximumSize) return legacyMaximumSize;
  }

  return null;
};

const getPointMaxSizeOrder = (point) => normalizePointMaximumSize(point)?.order ?? null;

const getPointMaxSizeCode = (point) => normalizePointMaximumSize(point)?.code ?? null;

const getPointMaxSizeLabel = (point) => {
  const maximumSize = normalizePointMaximumSize(point);
  return maximumSize?.description || maximumSize?.code || null;
};

const getSizeOrder = (size) => parseNumericValue(size?.order ?? size?.orden);

const getSizeCode = (size) => normalizeTextValue(size?.codigo ?? size?.code ?? size?.type, true);

const getSizeLabel = (size) => size?.descripcion || size?.description || getSizeCode(size) || null;

const getPointName = (point) => normalizeTextValue(point?.Name ?? point?.nombre ?? point?.NombrePunto);

const getPointId = (point) => parseNumericValue(point?.ScharffPointId ?? point?.puntoScharffId ?? point?.id);

// Point names come from the database and are rendered as HTML, so they are escaped here.
const escapeHtml = (value) => String(value ?? "").replace(/[&<>"']/g, (char) => ({
  "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
}[char]));

const bold = (value) => `<b>${escapeHtml(value)}</b>`;

// Same message without markup, for the places that render it as plain text.
const plain = (value) => String(value ?? "");

const getRequiredPoints = ({ shippingTypeId, originPoint = null, destinationPoint = null }) => {
  if (shippingTypeId === 1) return [{ point: originPoint, scopeLabel: "punto de origen" }];
  if (shippingTypeId === 2) return [{ point: destinationPoint, scopeLabel: "punto de destino" }];
  if (shippingTypeId === 3 || shippingTypeId === 5) {
    return [
      { point: originPoint, scopeLabel: "punto de origen" },
      { point: destinationPoint, scopeLabel: "punto de destino" },
    ];
  }
  return [];
};

// The modals reset the selected point to {}, which is truthy: without this check an unselected
// point reads as "capacity not configured" instead of "not selected yet".
const isPointSelected = (point) => !!point && Object.keys(point).length > 0;

const getPointCapacityConfigurationMessage = (point, scopeLabel = "Punto Sharf seleccionado") => {
  if (!isPointSelected(point)) return `Selecciona un ${scopeLabel.toLowerCase()} para validar la capacidad del paquete.`;
  if (!normalizePointMaximumSize(point)) {
    const name = getPointName(point);
    const target = name ? `El ${scopeLabel.toLowerCase()} "${name}"` : `El ${scopeLabel.toLowerCase()}`;
    return `${target} no tiene configurada su capacidad máxima. Selecciona otro punto o comunícate con soporte.`;
  }
  return "";
};

// Names the offending point when only one rejects the size; naming just one of two would mislead.
const isSamePoint = (first, second) => {
  const firstId = getPointId(first);
  const secondId = getPointId(second);
  // Names are compared only when neither side has an id.
  if (firstId !== null && secondId !== null) return firstId === secondId;
  if (firstId !== null || secondId !== null) return false;
  const name = getPointName(first);
  return !!name && name === getPointName(second);
};

const buildSizeRejectionMessage = (rejectingPoints, capacity, emphasise = bold) => {
  // Origin and destination can be the same point.
  const groups = [];
  rejectingPoints.forEach((entry) => {
    const group = groups.find((candidate) => isSamePoint(candidate.entry.point, entry.point));
    if (group) group.count += 1;
    else groups.push({ entry, count: 1 });
  });

  if (groups.length !== 1) {
    return `Solo se admite hasta ${emphasise(capacity.label)} en los Puntos Sharf seleccionados.`;
  }

  const [{ entry, count }] = groups;
  const { point, scopeLabel, maximumSize } = entry;
  const name = getPointName(point);
  // Neither scope alone describes a point that is both origin and destination.
  const label = count > 1 ? "Punto Sharf" : scopeLabel;

  return name
    ? `Solo se admite hasta ${emphasise(maximumSize.description)} en el ${label} ${emphasise(name)}.`
    : `Solo se admite hasta ${emphasise(maximumSize.description)} en el ${label}.`;
};

const getEffectivePointCapacity = ({ shippingTypeId, originPoint = null, destinationPoint = null }) => {
  const requiredPoints = getRequiredPoints({ shippingTypeId, originPoint, destinationPoint });
  if (requiredPoints.length === 0) {
    return {
      isConfigured: false,
      reason: "No se pudo determinar qué Puntos Sharf deben validar la capacidad del paquete.",
      order: null,
      label: null,
      points: [],
      scopeLabel: "este servicio",
    };
  }

  const normalizedPoints = requiredPoints.map(({ point, scopeLabel }) => ({
    point,
    scopeLabel,
    maximumSize: normalizePointMaximumSize(point),
  }));
  const pointWithConfigurationError = normalizedPoints.find(({ maximumSize }) => !maximumSize);
  if (pointWithConfigurationError) {
    return {
      isConfigured: false,
      reason: getPointCapacityConfigurationMessage(pointWithConfigurationError.point, pointWithConfigurationError.scopeLabel),
      order: null,
      label: null,
      points: normalizedPoints,
      scopeLabel: normalizedPoints.length > 1 ? "los Puntos Sharf seleccionados" : `este ${pointWithConfigurationError.scopeLabel}`,
    };
  }

  const effectivePoint = [...normalizedPoints]
    .sort((first, second) => first.maximumSize.order - second.maximumSize.order)[0];

  return {
    isConfigured: true,
    reason: "",
    order: effectivePoint.maximumSize.order,
    label: effectivePoint.maximumSize.description,
    points: normalizedPoints,
    scopeLabel: normalizedPoints.length > 1 ? "los Puntos Sharf seleccionados" : `este ${normalizedPoints[0].scopeLabel}`,
  };
};

const getConfiguredCapacityCompatibility = (capacity) => ({
  isAllowed: capacity.isConfigured,
  reason: capacity.reason,
  maxAllowedOrder: capacity.order,
  maxAllowedLabel: capacity.label,
});

const getManualSizeOptionCompatibility = (options) => (
  getConfiguredCapacityCompatibility(getEffectivePointCapacity(options))
);

const getSizeCompatibility = ({ size, shippingTypeId, originPoint = null, destinationPoint = null }) => {
  const capacity = getEffectivePointCapacity({ shippingTypeId, originPoint, destinationPoint });
  if (!capacity.isConfigured) return getConfiguredCapacityCompatibility(capacity);

  const sizeOrder = getSizeOrder(size);
  const sizeCode = getSizeCode(size);
  if (sizeOrder === null || !sizeCode) {
    return {
      isAllowed: false,
      reason: "No se pudo validar la talla del paquete porque no tiene código u orden configurado.",
      maxAllowedOrder: capacity.order,
      maxAllowedLabel: capacity.label,
    };
  }

  // Single cumulative size-order rule: P has the highest order, so it only fits P-capacity points.
  const rejectingPoints = capacity.points.filter(({ maximumSize }) => sizeOrder > maximumSize.order);
  const isAllowed = rejectingPoints.length === 0;

  return {
    isAllowed,
    reason: isAllowed ? "" : buildSizeRejectionMessage(rejectingPoints, capacity),
    reasonText: isAllowed ? "" : buildSizeRejectionMessage(rejectingPoints, capacity, plain),
    maxAllowedOrder: capacity.order,
    maxAllowedLabel: capacity.label,
  };
};

// "P" is not a selectable size: it is the manual-measurement mode, validated on the
// effective size returned by sizes/classify.
const decorateSizesWithCompatibility = (sizes = [], options = {}) => sizes
  .filter((size) => getSizeCode(size) !== CUSTOM_SIZE_CODE)
  .map((size) => {
    const compatibility = getSizeCompatibility({ size, ...options });

    return {
      ...size,
      isDisabled: !compatibility.isAllowed,
      // The size grid renders this as text, so it must carry no markup.
      disabledReason: compatibility.reasonText || compatibility.reason,
    };
  });

const isPointCapacityConfigured = (point) => !!normalizePointMaximumSize(point);

const getPointCapacityLabel = (point) => {
  if (!isPointSelected(point)) return null;

  const maximumSize = normalizePointMaximumSize(point);
  if (!maximumSize) return "Capacidad no configurada";

  return maximumSize.code === CUSTOM_SIZE_CODE
    ? "Acepta todas las tallas"
    : `Hasta talla ${maximumSize.code}`;
};

export {
  CUSTOM_SIZE_CODE,
  decorateSizesWithCompatibility,
  getManualSizeOptionCompatibility,
  getPointCapacityLabel,
  getPointCapacityConfigurationMessage,
  getPointMaxSizeCode,
  isPointCapacityConfigured,
  getPointMaxSizeLabel,
  getSizeCompatibility,
  getSizeLabel,
  normalizePointMaximumSize,
};
